import React, { useState } from 'react';
import { getUserProfile, deleteUser, batchSearchUsers, UserProfileResponse, DeleteUserRequest, BatchSearchUser, BatchSearchRequest } from '../../api/user-admin';

const UserManagement = () => {
    const [userId, setUserId] = useState('');
    const [userData, setUserData] = useState<UserProfileResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteReason, setDeleteReason] = useState('Requested in app');
    
    // Batch search state
    const [batchResults, setBatchResults] = useState<BatchSearchUser[]>([]);
    const [batchLoading, setBatchLoading] = useState(false);
    const [batchFilters, setBatchFilters] = useState<BatchSearchRequest>({
        ambassadorStatus: undefined,
        premiumStatus: undefined,
        postcodePrefix: ''
    });

    const handleSearch = async () => {
        if (!userId.trim()) {
            setError('Please enter a user ID or email');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');
        setUserData(null);

        try {
            const response = await getUserProfile(userId.trim());
            setUserData(response);
            setSuccess(`Found user: ${response.email || 'No email'}`);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleBatchSearch = async () => {
        setBatchLoading(true);
        setError('');
        setSuccess('');
        setBatchResults([]);

        try {
            const response = await batchSearchUsers(batchFilters);
            setBatchResults(response.users);
            setSuccess(`Found ${response.count} users (limit: ${response.limit})`);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setBatchLoading(false);
        }
    };

    const handleExportCSV = () => {
        if (batchResults.length === 0) {
            setError('No results to export');
            return;
        }

        const headers = ['User ID', 'Email', 'Name', 'Address'];
        const rows = batchResults.map(user => {
            const name = `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim() || '-';
            const address = user.profile?.streetAddress || '';
            const postcode = user.profile?.postcode || '';
            const fullAddress = [address, postcode].filter(Boolean).join(', ') || '-';
            return [
                user.userId,
                user.email || '',
                name,
                fullAddress
            ];
        });

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDeleteUser = async () => {
        if (!userData || !deleteReason.trim()) {
            setError('Please provide a reason for deletion');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const request: DeleteUserRequest = {
                reason: deleteReason.trim()
            };

            const response = await deleteUser(userData.userId, request);
            setSuccess(`Successfully deleted user ${response.userEmail} (${response.deletedUserId})`);
            setUserData(null);
            setShowDeleteConfirm(false);
            setDeleteReason('Requested in app');
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const confirmDelete = () => {
        if (!userData) return;
        setShowDeleteConfirm(true);
    };

    const cancelDelete = () => {
        setShowDeleteConfirm(false);
        setDeleteReason('Requested in app');
    };

    return (
        <div className="max-w-4xl mx-auto py-8">
            <div className="bg-white shadow-lg rounded-lg p-6">
                <h2 className="text-2xl font-bold mb-6 text-gray-800">User Management</h2>
                
                {/* Individual Search Section */}
                <div className="mb-8">
                    <h3 className="text-lg font-semibold mb-4 text-gray-700">Search User</h3>
                    <div className="flex space-x-2">
                        <input
                            type="text"
                            value={userId}
                            onChange={(e) => setUserId(e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter user ID or email"
                            disabled={loading}
                        />
                        <button
                            onClick={handleSearch}
                            disabled={loading || !userId.trim()}
                            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Searching...' : 'Search'}
                        </button>
                    </div>
                </div>

                {/* Batch Search Section */}
                {/*
                <div className="mb-8 border-t pt-8">
                    <h3 className="text-lg font-semibold mb-4 text-gray-700">Batch Search Users</h3>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                        <p className="text-sm text-yellow-800">
                            <strong>Note:</strong> This is an MVP version of the batch search feature. 
                            The search may take several minutes to complete as it iterates through all users. 
                            Please be patient and do not refresh the page.
                        </p>
                    </div>
                    {batchLoading && (
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
                            <div className="flex items-center space-x-3">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                                <p className="text-sm text-blue-800 font-medium">
                                    Searching through all users... This may take several minutes. Please wait.
                                </p>
                            </div>
                        </div>
                    )}
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Ambassador Status</label>
                                <select
                                    value={batchFilters.ambassadorStatus === undefined ? '' : batchFilters.ambassadorStatus ? 'true' : 'false'}
                                    onChange={(e) => setBatchFilters({
                                        ...batchFilters,
                                        ambassadorStatus: e.target.value === '' ? undefined : e.target.value === 'true'
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    disabled={batchLoading}
                                >
                                    <option value="">All</option>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Premium Status</label>
                                <select
                                    value={batchFilters.premiumStatus === undefined ? '' : batchFilters.premiumStatus ? 'true' : 'false'}
                                    onChange={(e) => setBatchFilters({
                                        ...batchFilters,
                                        premiumStatus: e.target.value === '' ? undefined : e.target.value === 'true'
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    disabled={batchLoading}
                                >
                                    <option value="">All</option>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Postcode Prefix (1-2 chars)</label>
                                <input
                                    type="text"
                                    value={batchFilters.postcodePrefix || ''}
                                    onChange={(e) => setBatchFilters({
                                        ...batchFilters,
                                        postcodePrefix: e.target.value.toUpperCase().slice(0, 2)
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., SW, N"
                                    maxLength={2}
                                    disabled={batchLoading}
                                />
                            </div>
                        </div>
                        <div className="flex space-x-2">
                            <button
                                onClick={handleBatchSearch}
                                disabled={batchLoading}
                                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                            >
                                {batchLoading && (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                )}
                                <span>{batchLoading ? 'Searching...' : 'Search Users'}</span>
                            </button>
                            {batchResults.length > 0 && (
                                <button
                                    onClick={handleExportCSV}
                                    className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                                >
                                    Export CSV ({batchResults.length} users)
                                </button>
                            )}
                        </div>
                    </div>
                </div>*/}

                {/* Batch Results Table */}
                {/*
                {batchResults.length > 0 && (
                    <div className="mb-8 border-t pt-8">
                        <h3 className="text-lg font-semibold mb-4 text-gray-700">Search Results ({batchResults.length} users)</h3>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">User ID</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Email</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Name</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Address</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {batchResults.map((user) => {
                                        const name = `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim() || '-';
                                        const address = user.profile?.streetAddress || '';
                                        const postcode = user.profile?.postcode || '';
                                        const fullAddress = [address, postcode].filter(Boolean).join(', ') || '-';
                                        return (
                                            <tr key={user.userId} className="hover:bg-gray-50">
                                                <td className="px-3 py-2 text-xs text-gray-900 font-mono border-b">{user.userId.substring(0, 20)}...</td>
                                                <td className="px-3 py-2 text-xs text-gray-900 border-b">{user.email || '-'}</td>
                                                <td className="px-3 py-2 text-xs text-gray-900 border-b">{name}</td>
                                                <td className="px-3 py-2 text-xs text-gray-900 border-b">{fullAddress}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
                */}

                {/* User Data Display */}
                {userData && (
                    <div className="mb-8">
                        <h3 className="text-lg font-semibold mb-4 text-gray-700">User Information</h3>
                        <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-600">User ID</label>
                                    <p className="text-sm text-gray-900 font-mono">{userData.userId}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600">Email</label>
                                    <p className="text-sm text-gray-900">{userData.email || 'No email'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600">Balance</label>
                                    <p className="text-sm text-gray-900">{userData.balance} points</p>
                                </div>
                            </div>

                            {userData.profile && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-2">Profile Data</label>
                                    <div className="bg-white border rounded p-3">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div>
                                                <span className="text-xs text-gray-500">First Name:</span>
                                                <p className="text-sm">{userData.profile.firstName || 'Not set'}</p>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-500">Last Name:</span>
                                                <p className="text-sm">{userData.profile.lastName || 'Not set'}</p>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-500">Street Address:</span>
                                                <p className="text-sm">{userData.profile.streetAddress || 'Not set'}</p>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-500">Postcode:</span>
                                                <p className="text-sm">{userData.profile.postcode || 'Not set'}</p>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-500">Country Code:</span>
                                                <p className="text-sm">{userData.profile.countryCode || 'Not set'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={confirmDelete}
                                    disabled={loading}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Delete User
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                            <h3 className="text-lg font-semibold mb-4 text-red-600">Confirm User Deletion</h3>
                            <p className="text-sm text-gray-600 mb-4">
                                Are you sure you want to delete user <strong>{userData?.email || userData?.userId}</strong>? 
                                This action cannot be undone and will permanently delete their Firebase account.
                            </p>
                            
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Reason for deletion *
                                </label>
                                <textarea
                                    value={deleteReason}
                                    onChange={(e) => setDeleteReason(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                    placeholder="Enter reason for deletion..."
                                    rows={3}
                                    disabled={loading}
                                />
                            </div>

                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={cancelDelete}
                                    disabled={loading}
                                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteUser}
                                    disabled={loading || !deleteReason.trim()}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? 'Deleting...' : 'Delete User'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Messages */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                        <p className="text-sm text-red-800">{error}</p>
                    </div>
                )}

                {success && (
                    <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
                        <p className="text-sm text-green-800">{success}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserManagement;
